import type { InstanceSeatsView } from "@keydials/api-client";

/**
 * How a licence position is put into words (KEY-497).
 *
 * A pure module for the same reason `grantFeedback.ts` and `memberRows.ts` are: the decisions here — which
 * of three states a seat view is in, how a pool that is a MULTIPLE gets explained, when a number is a lie —
 * are the ones worth testing, and web-user has no DOM harness to test them through a rendered panel.
 *
 * It is also the single place the wording lives. The People sheet's one-line seat sentence and the
 * Licensing panel's full read of the same figure both come from here, so the two cannot drift into saying
 * different things about the same plan.
 */

/**
 * The three states `InstanceSeatsView` documents. None may be collapsed into another:
 *
 * - `unenforced` — nothing is counting. `used` is null because there is no subtree to count over, so this
 *   must never render as "0 of 0", which would be a number nobody can stand behind.
 * - `unlimited` — governed, but the plan has no ceiling. `used` is real and worth showing.
 * - `counted` — the ordinary case: N of M gone.
 */
export type LicensingState = "unenforced" | "unlimited" | "counted";

export interface LicensingSummary {
  state: LicensingState;
  /** The headline sentence. Always present — every state has something true to say. */
  headline: string;
  /** Places left, when there is a ceiling to have any left of. */
  remaining: number | null;
  /** Every place is taken. The panel warns, and the People sheet stops offering to invite. */
  full: boolean;
}

export function licensingState(seats: InstanceSeatsView | null | undefined): LicensingState {
  if (!seats?.enforced) return "unenforced";
  return seats.allowance == null || seats.used == null ? "unlimited" : "counted";
}

/** Pluralises around a count: `plural(1, "place")` → "place", `plural(2, "place")` → "places". */
function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

/**
 * The headline, and whether the plan is full.
 *
 * Note what the wording deliberately does NOT say: "on this dial". The figure counts the whole subscriber
 * subtree, so a label naming this dial would be wrong — it is N people on the account, across everything the
 * subscription covers.
 */
export function licensingSummary(seats: InstanceSeatsView | null | undefined): LicensingSummary {
  const state = licensingState(seats);

  if (state === "unenforced") {
    return {
      state,
      headline: "Nobody is counting users on this dial.",
      remaining: null,
      full: false,
    };
  }

  const used = seats!.used ?? 0;

  if (state === "unlimited") {
    return {
      state,
      headline: `Your licence includes unlimited users. ${used} ${used === 1 ? "person is" : "people are"} using the dial today.`,
      remaining: null,
      full: false,
    };
  }

  const allowance = seats!.allowance!;
  const remaining = Math.max(0, allowance - used);

  return {
    state,
    headline:
      remaining === 0
        ? `All ${allowance} ${plural(allowance, "place")} on your licence are being used.`
        : `${used} of ${allowance} ${plural(allowance, "place")} used — ${remaining} left.`,
    remaining,
    full: remaining === 0,
  };
}

/**
 * Where the pool came from, in the customer's terms — or null when there is nothing to explain.
 *
 * This is the line that makes the number readable. `usersPerUnit` is not a seat total: it is the numerator
 * of a multiplier whose denominator varies by plan family, so a pool GROWS when somebody builds a dial. An
 * administrator who is not told that reads a figure which moved on its own.
 */
export function poolDerivation(seats: InstanceSeatsView | null | undefined): string | null {
  if (!seats?.enforced || seats.usersPerUnit == null || seats.units == null) return null;

  const per = seats.usersPerUnit;
  const units = seats.units;
  const unit = unitNoun(seats.allowanceUnit);
  if (!unit) return null;

  return `${per} ${plural(per, "user")} per ${unit.singular} × ${units} ${plural(units, unit.singular)}`;
}

/** How a plan's allowance unit reads to somebody who has never seen the billing model. */
function unitNoun(allowanceUnit: string | null | undefined): { singular: string } | null {
  switch (allowanceUnit) {
    case "Instance":
      return { singular: "dial" };
    case "Definition":
      // The customer-facing word for a definition is the dial they built, not the instance they run.
      return { singular: "dial design" };
    case "School":
      return { singular: "school" };
    default:
      return null;
  }
}

/**
 * The included/purchased split, when anything was actually bought. Null when the whole pool came with the
 * plan — saying "0 bought" on every ordinary account is noise.
 */
export function purchasedLine(seats: InstanceSeatsView | null | undefined): string | null {
  if (!seats?.enforced) return null;
  const purchased = seats.purchasedAllowance ?? 0;
  const included = seats.includedAllowance;
  if (purchased <= 0 || included == null) return null;

  return `${included} included with your plan, plus ${purchased} bought on top.`;
}

/**
 * How many of the places in use belong to archived accounts, when any do.
 *
 * The likeliest real answer to "why are we full?", and visible nowhere else: archiving somebody blocks their
 * sign-in but deliberately leaves their dial memberships alone, so they go on occupying a place until
 * somebody removes them from the dials themselves.
 */
export function archivedLine(seats: InstanceSeatsView | null | undefined): string | null {
  const archived = seats?.enforced ? seats.archivedInUse ?? 0 : 0;
  if (archived <= 0) return null;

  return archived === 1
    ? "1 of these is an archived account. Archiving somebody stops them signing in, but leaves their dial access — and its place — in use."
    : `${archived} of these are archived accounts. Archiving somebody stops them signing in, but leaves their dial access — and its place — in use.`;
}

/**
 * What counts as using a place. Stated on the panel rather than left to be discovered from a refusal,
 * because every clause here has surprised somebody: read-only is not free (KEY-474), part of a dial costs
 * the same as all of it, and one person on five dials is one place, not five.
 */
export const LICENCE_RULES: readonly string[] = [
  "Everybody you add to a dial uses a place, including people who can only read.",
  "Access to part of a dial still uses a place.",
  "One person uses one place, however many dials they are on.",
  "Guests are free, and never use a place.",
  "Places are counted across your whole account, not just this dial.",
];
